import { jwtDecode } from 'jwt-decode';
import {
  createStytchB2BHeadlessClient,
  mount,
  B2BProducts,
  AuthFlowType,
  StytchEventType,
} from '@stytch/vanilla-js/b2b';
import { mountAdminPortalSSO as mountAdminPortalSSOWidget } from '@stytch/vanilla-js/b2b/adminPortal';
import auxoLogoUrl from '@/assets/svg/auxo-logo-blue.svg?url';
import router from '../../router/index';
import { setTokens, clearTokens } from './session';

const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'cognito';

const stytchPublicToken = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;
const stytchRedirectUri = import.meta.env.VITE_STYTCH_REDIRECT_URI;
const stytchTokenUrl = import.meta.env.VITE_STYTCH_TOKEN_URL;
const stytchConnectionId = import.meta.env.VITE_STYTCH_SSO_CONNECTION_ID;
// VITE_STYTCH_ORGANIZATION_SLUG is intentionally not read here: the Discovery
// flow resolves the organization from the member's email rather than being
// pinned to one. The Organization-flow variant on 5529-Stytch-OrgFlow does use
// it, and frontend_build.py still emits it for both.
const stytchTrustedAuthProfileId = import.meta.env
  .VITE_STYTCH_TRUSTED_AUTH_PROFILE_ID;
const stytchOrganizationId = import.meta.env.VITE_STYTCH_ORGANIZATION_ID;

// Initialise Stytch B2B client whenever this client has been onboarded to
// Stytch at all — not just when it's the active login provider. Cognito
// clients still need a live client to bootstrap an admin-portal session
// (see mountAdminPortalSSO below).
const stytch = stytchPublicToken
  ? createStytchB2BHeadlessClient(stytchPublicToken)
  : null;

// org_slug → connection_id, emitted by frontend_build.py from
// /{deployment_id}/stytch/info. Two entries on a mixed deployment.
const stytchSsoConnections = (() => {
  try {
    return JSON.parse(import.meta.env.VITE_STYTCH_SSO_CONNECTIONS || '{}');
  } catch {
    return {};
  }
})();

// Which SSO connection ?action=sso-login should start. Bare = this deployment's
// own org; ?org= targets another it admits. The map IS the allowlist, so an
// unknown slug returns '' and the caller must render the login page instead —
// also the pre-SSO state where a first admin needs the password form.
export function resolveSsoConnectionId(orgSlug) {
  if (AUTH_PROVIDER !== 'stytch' || !stytch) return '';
  if (!orgSlug) return stytchConnectionId || '';

  // Own-property + string check, not a bare lookup: '__proto__' resolves to
  // Object.prototype and 'constructor' to a function, both truthy, so a bare
  // lookup would hand signIn a non-string and strand the user on a cancelled
  // navigation with no login form.
  if (!Object.hasOwn(stytchSsoConnections, orgSlug)) return '';
  const connectionId = stytchSsoConnections[orgSlug];
  return typeof connectionId === 'string' ? connectionId : '';
}

export async function signIn(connectionId = stytchConnectionId) {
  // Report failure so the caller can render the login page — otherwise the
  // router cancels the navigation and the user gets no form at all.
  if (!stytch || !connectionId) return false;

  // Clear any existing Stytch session and local tokens so we don't inherit a cached user
  clearTokens();
  try {
    if (stytch.session.getSync()) await stytch.session.revoke();
  } catch {
    /* session may already be expired */
  }

  // Redirects browser → Stytch → Okta → back to stytchRedirectUri
  // Callback will have ?stytch_token_type=sso&token=...
  stytch.sso.start({
    connection_id: connectionId,
    login_redirect_url: stytchRedirectUri,
    signup_redirect_url: stytchRedirectUri,
  });
  return true;
}

export async function signOut() {
  try {
    await stytch.session.revoke();
  } catch {
    /* session may already be expired or revoked */
  }
  clearTokens();
  window.location.replace('/logout');
}

// Cognito's own signOut has no knowledge that mountAdminPortalSSO may have
// bootstrapped a live Stytch session (e.g. via a shared browser). Called
// from the facade's signOut regardless of active provider so that session
// doesn't outlive this app's logout — no-op if none exists.
export async function revokeBootstrappedSession() {
  if (!stytch) return;
  try {
    if (stytch.session.getSync()) await stytch.session.revoke();
  } catch {
    /* session may already be expired or revoked */
  }
}

export async function getTokens(code, refresh_token) {
  return code ? _getStytchTokens(code) : _refreshStytchToken(refresh_token);
}

// ssoToken = the SSO token from ?token= in the Stytch callback URL
async function _getStytchTokens(ssoToken) {
  try {
    // Exchange SSO token → Stytch session. SDK handles the PKCE verifier automatically.
    const authResp = await stytch.sso.authenticate({
      sso_token: ssoToken,
      session_duration_minutes: 60,
    });

    // Send session to our backend for DB lookup + custom claim enrichment.
    const response = await fetch(stytchTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_token: authResp.session_token,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const { data } = await response.json();
    setTokens(data.id_token, data.access_token, data.refresh_token);
  } catch (error) {
    console.error('Failed to get Stytch tokens:', error);
    throw error;
  }
}

// sessionToken = stored refresh_token (Stytch session token from prior auth)
async function _refreshStytchToken(sessionToken) {
  try {
    const response = await fetch(stytchTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: sessionToken }),
    });

    if (!response.ok)
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    const { data } = await response.json();
    setTokens(data.id_token, data.access_token, data.refresh_token);
  } catch (error) {
    console.error('Failed to refresh Stytch token:', error);
    throw error;
  }
}

export async function isAuthenticated() {
  const token = sessionStorage.getItem('id_token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return Date.now() < exp * 1000;
  } catch {
    return false;
  }
}

// The /login widget: Discovery flow, so the email routes to the right IdP with no
// organization pinned. Not the path the Okta tile takes (?action=sso-login skips
// this entirely) — this is a cold arrival, after a logout or from the
// unauthorized screen. Gated by the caller via VITE_STYTCH_MANUAL_LOGIN = 1.
export function mountManualLogin(elementId) {
  if (AUTH_PROVIDER !== 'stytch') return;

  // Password-reset-by-email links redirect back here with
  // ?stytch_token_type=multi_tenant_passwords&token=... — per Stytch's docs,
  // completing that flow requires mounting with authFlowType: PasswordReset
  // rather than our normal login config.
  const isPasswordResetRedirect =
    new URL(window.location.href).searchParams.get('stytch_token_type') ===
    'multi_tenant_passwords';

  const config = isPasswordResetRedirect
    ? {
        authFlowType: AuthFlowType.PasswordReset,
        products: [B2BProducts.passwords],
        sessionOptions: { sessionDurationMinutes: 60 },
      }
    : {
        // No organizationSlug: Discovery resolves the organization from the
        // email instead of being told it up front. That is the whole point of
        // this variant, and what makes one login page able to serve both
        // Revology staff and a client's own users.
        authFlowType: AuthFlowType.Discovery,
        products: [B2BProducts.sso, B2BProducts.passwords],
        sessionOptions: { sessionDurationMinutes: 60 },
        ssoOptions: {
          loginRedirectURL: stytchRedirectUri,
          signupRedirectURL: stytchRedirectUri,
        },
        // Password reset/signup links must redirect back to a page with this
        // same widget mounted (not the SSO-only /status callback) — see
        // isPasswordResetRedirect above for how we handle the return trip.
        //
        // discoveryRedirectURL is required, not optional — unset, Stytch falls
        // back to a project default we cannot have. All three intentionally
        // ignore BASE_URL: only unversioned paths are allowlisted.
        passwordOptions: {
          loginRedirectURL: `${window.location.origin}/login`,
          resetPasswordRedirectURL: `${window.location.origin}/login`,
          discoveryRedirectURL: `${window.location.origin}/login`,
        },
        // Skip the organization picker when the member belongs to exactly one
        // org, which is every user today — without this, a single-org member
        // would be shown a one-item list to choose from. ignore* keep that
        // shortcut in place even when Stytch could instead offer them a pending
        // invite or a JIT-provisionable org.
        directLoginForSingleMembership: {
          status: true,
          ignoreInvites: true,
          ignoreJitProvisioning: true,
        },
        // Members are provisioned by us (invite or SSO JIT), never self-serve.
        // This only hides the affordance — sign-up is actually off via the
        // project's basic.create_new_members and the org's
        // email_jit_provisioning. See CLAUDE.md ("Sign-up and password reset").
        disableCreateOrganization: true,
      };

  const loginProps = {
    client: stytch,
    config,
    presentation: {
      theme: {
        'container-width': '480px',
        primary: '#004b87', // only accepts hex values
        'primary-foreground': '#ffffff',
      },
      options: {
        logo: { url: auxoLogoUrl, alt: 'Auxo by Revology' },
        hideHeaderText: true,
      },
    },
    callbacks: {
      onEvent: message => {
        if (message.type === StytchEventType.AuthenticateFlowComplete) {
          completeManualLogin();
        }
      },
    },
  };
  const node = mount({ elementId, ...loginProps });
  node.render(loginProps);
}

async function completeManualLogin() {
  try {
    const tokens = stytch.session.getTokens();
    if (!tokens?.session_token) {
      throw new Error('No active Stytch session after manual login');
    }

    const response = await fetch(stytchTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: tokens.session_token }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const { data } = await response.json();
    setTokens(data.id_token, data.access_token, data.refresh_token);
    router.push('/status');
  } catch (error) {
    console.error('Failed to complete manual login:', error);
  }
}

// When AUTH_PROVIDER=stytch
//   is already Stytch-session-authenticated from normal login.
// When AUTH_PROVIDER=cognito
//   no Stytch session exists yet, so we bootstrap one by
//   exchanging the caller's already-verified Cognito id_token for a Stytch
//   session via a Trusted Auth Token profile
export async function mountAdminPortalSSO(elementId) {
  if (!stytch) return; // Stytch not onboarded for this client at all

  if (AUTH_PROVIDER !== 'stytch' && !stytch.session.getSync()) {
    const cognitoIdToken = sessionStorage.getItem('id_token');
    if (!cognitoIdToken || !stytchTrustedAuthProfileId) return;

    try {
      await stytch.session.attest({
        profile_id: stytchTrustedAuthProfileId,
        organization_id: stytchOrganizationId,
        token: cognitoIdToken,
        session_duration_minutes: 60,
      });
    } catch (error) {
      console.error('Failed to bootstrap Stytch admin session:', error);
      return;
    }
  }

  mountAdminPortalSSOWidget({ client: stytch, element: elementId });
}
